import type { Permission } from '@iot-middleware/shared'

export type DeviceTypeConfig = {
  main?: string
  addingConfiguration?: string
  postCreation?: string
}

export type ApplicationConfig = {
  _id: string
  name: string
  components: Partial<Record<'main' | 'addingConfiguration', string>>
  deviceHeaders: {
    title: string
    field: string
  }[]
  deviceTypes?: Record<string, DeviceTypeConfig>
  enabledExchangeDevices?: string[]
  // probably unused?
  devices?: {
    _id: string
    title: string
    component: string
  }[]
}
export type AppConfig = {
  url: string
  publicPath: string
  appTitle: string
  applications: ApplicationConfig[]
  apiKeyPermissionMapping: {
    _id: string
    name: string
    permissions: Permission[]
  }[]
  screens: {
    title: string
    path: string
    icon: string
  }[]
  time: unknown
}

const APP_CONFIG: AppConfig = {
  appTitle: 'IoT Middleware',
  publicPath: '/',
  url: 'http://localhost/api',
  applications: [
    {
      _id: 'ttn-v3',
      name: 'The Things Network',
      components: {
        main: 'ttn-v3/Overview',
        addingConfiguration: 'ttn-v3/AddingConfiguration',
      },
      deviceHeaders: [
        {
          title: 'LoRa Device EUI',
          field: 'metaData.lorawan.deviceEUI',
        },
      ],
      deviceTypes: {
        'generic-lora': {
          main: 'generic-lore/Overview',
        },
        'iota-lora': {
          addingConfiguration: 'iota-lora/AddingConfiguration',
          main: 'iota-lora/Overview',
        },
      },
    },
    {
      _id: 'tp',
      name: 'ThingPark Network',
      components: {
        main: 'tp/Overview',
        addingConfiguration: 'tp/AddingConfiguration',
      },
      deviceHeaders: [
        {
          title: 'LoRa Device EUI',
          field: 'metaData.lorawan.deviceEUI',
        },
      ],
    },
    {
      _id: 'ontoto',
      name: 'Ontoto Network',
      components: {
        main: 'ontoto/Overview',
        addingConfiguration: 'ontoto/AddingConfiguration',
      },
      deviceHeaders: [
        {
          title: 'Serial Number',
          field: 'metaData.serialNumber',
        },
      ],
      deviceTypes: {
        ontoto: {
          main: 'ontoto/Overview',
          addingConfiguration: 'ontoto/AddingConfiguration',
        },
      },
    },
    {
      _id: 'addupi',
      name: 'Adcon Network',
      components: {
        main: 'addupi/Overview',
        addingConfiguration: 'addupi/AddingConfiguration',
      },
      deviceTypes: {
        A764: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A765: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A760: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A755GSM: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A753: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A733: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A723_S4: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        A753GSM: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
        RA440: {
          main: 'adcon/Overview',
          addingConfiguration: 'adcon/AddingConfiguration',
        },
      },
      deviceHeaders: [
        {
          title: 'Serial Number',
          field: 'metaData.addupi.serialNumber',
        },
      ],
    },
    {
      _id: 'mqtt',
      name: 'MQTT Network',
      components: {
        addingConfiguration: 'mqtt/AddingConfiguration',
      },
      deviceHeaders: [
        {
          title: 'Identifier',
          field: 'metaData.identifier',
        },
        {
          title: 'Serial Number',
          field: 'serialNumber',
        },
      ],
      deviceTypes: {
        'ml-iot': {
          addingConfiguration: 'ml-iot/AddingConfiguration',
        },
        'iota-2dl': {
          addingConfiguration: 'iota-2dl/AddingConfiguration',
          postCreation: 'iota-2dl/PostCreation',
        },
        'campbell-mqtt': {
          addingConfiguration: 'campbell-mqtt/AddingConfiguration',
          postCreation: 'campbell-mqtt/PostCreation',
        },
        'hyquant-edge': {
          addingConfiguration: 'edge/AddingConfiguration',
          postCreation: 'edge/PostCreation',
          main: 'edge/Overview',
        },
        'hailsens-edge': {
          addingConfiguration: 'edge/AddingConfiguration',
          postCreation: 'edge/PostCreation',
          main: 'edge/Overview',
        },
      },
    },
    {
      _id: 'ottml',
      name: 'OTT-ML HTTP Network',
      components: {},
      deviceHeaders: [
        {
          title: 'Device ID (OTT-ML)',
          field: 'metaData.identifier',
        },
        {
          title: 'Device Name (OTT-ML)',
          field: 'metaData.name',
        },
      ],
      deviceTypes: {
        ecolog: {
          addingConfiguration: 'ecolog/AddingConfiguration',
          main: 'ecolog/Overview',
        },
        netdl: {
          addingConfiguration: 'netdl/AddingConfiguration',
          main: 'netdl/Overview',
          postCreation: 'netdl/PostCreation',
        },
      },
    },
    {
      _id: 'iota-server',
      name: 'IoTa LTE-M Network',
      components: {},
      deviceHeaders: [
        {
          title: 'Device ID',
          field: 'metaData.identifier',
        },
        {
          title: 'IMEI',
          field: 'metaData.imei',
        },
      ],
      devices: [
        {
          _id: 'iota-lte',
          title: 'IoTa LTE-M',
          component: 'iota-lte/Overview',
        },
      ],
      deviceTypes: {
        'iota-lte': {
          addingConfiguration: 'iota-lte/AddingConfiguration',
          main: 'iota-lte/Overview',
          postCreation: 'iota-lte/PostCreation',
        },
      },
    },
    {
      _id: 'ilevel-server',
      name: 'iLevel-GW Network',
      components: {},
      enabledExchangeDevices: ['ilevel-gw-4g'],
      deviceHeaders: [
        {
          title: 'Mac Address',
          field: 'metaData.macAddress',
        },
      ],
      deviceTypes: {
        'ilevel-gw-2g': {
          addingConfiguration: 'ilevel-gw-2g/AddingConfiguration',
          main: 'ilevel-gw-2g/Overview',
          postCreation: 'ilevel-gw-2g/PostCreation',
        },
        'ilevel-gw-4g': {
          addingConfiguration: 'ilevel-gw-4g-universal/AddingConfiguration',
          main: 'ilevel-gw-4g-universal/Overview',
          postCreation: 'ilevel-gw-4g-universal/PostCreation',
        },
        'ilevel-gw-4g-1500': {
          addingConfiguration: 'ilevel-gw-4g-universal/AddingConfiguration',
          main: 'ilevel-gw-4g-universal/Overview',
          postCreation: 'ilevel-gw-4g-universal/PostCreation',
        },
        'ilevel-gw-4g-1700': {
          addingConfiguration: 'ilevel-gw-4g-universal/AddingConfiguration',
          main: 'ilevel-gw-4g-universal/Overview',
          postCreation: 'ilevel-gw-4g-universal/PostCreation',
        },
      },
    },
    {
      _id: 'cae-ftp',
      name: 'CAE Network',
      components: {
        main: 'cae/Overview',
        addingConfiguration: 'cae/AddingConfiguration',
      },
      deviceHeaders: [],
      deviceTypes: {
        cae: {
          main: 'cae/Overview',
        },
      },
    },
    {
      _id: 'ftp-app-zrxp',
      name: 'FTP Network - ZRXP',
      components: {
        main: 'common/FtpOverview',
        addingConfiguration: 'common/FtpGatewayConfig',
      },
      deviceTypes: {
        'ftp-device-zrxp': {
          main: 'ftp-device-zrxp/Overview',
          addingConfiguration: 'ftp-device-zrxp/AddingConfiguration',
        },
      },
      deviceHeaders: [],
    },
    {
      _id: 'ftp-app-mis',
      name: 'FTP Network - MIS',
      components: {
        main: 'common/FtpOverview',
        addingConfiguration: 'ftp-app-mis/MisFtpConfig',
      },
      deviceTypes: {
        'ftp-device-mis': {
          main: 'ftp-device-mis/Overview',
          addingConfiguration: 'ftp-device-mis/AddingConfiguration',
        },
      },
      deviceHeaders: [],
    },
    {
      _id: 'ftp-app-vulink-csv',
      name: 'FTP Network - VuLink CSV',
      components: {
        main: 'common/FtpOverview',
        addingConfiguration: 'common/FtpGatewayConfig',
      },
      deviceTypes: {
        'ftp-device-vulink-csv': {
          main: 'ftp-device-vulink-csv/Overview',
          addingConfiguration: 'ftp-device-vulink-csv/AddingConfiguration',
        },
      },
      deviceHeaders: [],
    },
    {
      _id: 'ftp-app-lns-csv',
      name: 'FTP Network - LNS CSV',
      components: {
        main: 'common/FtpOverview',
        addingConfiguration: 'common/FtpGatewayConfig',
      },
      deviceTypes: {
        'ftp-device-lns-csv': {
          main: 'ftp-device-lns-csv/Overview',
          addingConfiguration: 'ftp-device-lns-csv/AddingConfiguration',
        },
      },
      deviceHeaders: [],
    },
    {
      _id: 'hailsens',
      name: 'HailSens Network',
      components: {
        main: 'hailsens/Overview',
        addingConfiguration: 'hailsens/AddingConfiguration',
      },
      deviceHeaders: [
        {
          title: 'Device ID',
          field: 'metaData.identifier',
        },
      ],
      deviceTypes: {
        hailsens: {
          main: 'hailsens/Overview',
          addingConfiguration: 'hailsens/AddingConfiguration',
          postCreation: 'hailsens/PostCreation',
        },
      },
    },
  ],
  apiKeyPermissionMapping: [
    {
      _id: 'admin',
      name: 'Local Organisation Administrator',
      permissions: [
        'organisations:get',
        'applications:get',
        'applications:create',
        'applications:edit',
        'applications:remove',
        'devices:get',
        'devices:create',
        'devices:edit',
        'devices:remove',
        'deviceTypes:get',
        'deviceLogs:get',
        'deviceLogs:create',
        'users:get',
        'networkAdapterHosts:get',
        'mqttIntegrationKeys:get',
        'firmware:get',
      ],
    },
    {
      _id: 'device-registration',
      name: 'Device Registration Key (HyComm)',
      permissions: [
        'organisations:get',
        'applications:get',
        'devices:get',
        'devices:create',
        'devices:edit',
        'devices:remove',
        'deviceTypes:get',
        'deviceLogs:get',
        'deviceLogs:create',
        'networkAdapterHosts:get',
        'mqttIntegrationKeys:get',
        'firmware:get',
      ],
    },
    {
      _id: 'viewer',
      name: 'Viewing Key',
      permissions: [
        'organisations:get',
        'applications:get',
        'devices:get',
        'deviceTypes:get',
        'deviceLogs:get',
        'users:get',
        'networkAdapterHosts:get',
        'mqttIntegrationKeys:get',
        'firmware:get',
      ],
    },
  ],
  screens: [
    {
      title: 'Overview',
      path: 'overview',
      icon: 'mdi-card-search',
    },
    {
      title: 'Networks',
      path: 'applications',
      icon: 'mdi-wan',
    },
    // {
    //   title: 'Devices',
    //   path: 'devices',
    //   icon: 'mdi-chip'
    // },
    {
      title: 'Data consumers',
      path: 'data-consumers',
      icon: 'mdi-database-arrow-right',
    },
  ],
  time: {
    /**
     * Timezone: If empty will default to local browser timezone, https://en.wikipedia.org/wiki/List_of_tz_database_time_zones
     * @type {String}
     */
    // timezone: 'Europe/Berlin',
    /**
     * Default: Must be in the format: 'YYYY-MM-DD HH:mm'
     * @type {String}
     */
    default: {
      dateType: 'day',
    },
    /**
     * Locale: Defines way time is displayed
     * @type {String}
     */
    locale: 'en-gb',
    /**
     * ClockFormat: 24hr, ampm, @TODO browser (automatic)
     * @type {String}
     */
    clockFormat: '24hr',
    /**
     * EnabledDateTypes: date types that are available to users
     * @type {Array[{String}]}
     */
    enabledDateTypes: ['hour', 'day', 'month', 'year'],
  },
} as const
export default APP_CONFIG
